import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';

/**
 * Permission-based authorization hook.
 * Always check permissions (not roles) for UI gating.
 * Roles are archetypes — they assign permissions, but code checks permissions.
 */
export function usePermission() {
  const user = useAuthStore((s) => s.user);

  const permissionSet = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  /** Check if the user has a specific permission. */
  const can = (permission: string): boolean => permissionSet.has(permission);

  /** Check if the user has ANY of the listed permissions. */
  const canAny = (permissions: string[]): boolean =>
    permissions.some((p) => permissionSet.has(p));

  /** Check if the user has ALL of the listed permissions. */
  const canAll = (permissions: string[]): boolean =>
    permissions.every((p) => permissionSet.has(p));

  return { can, canAny, canAll };
}
